import { defineStore } from 'pinia'

import { fakeRouterBrands, fakeRouterOthers, fakeRouterServices } from '@/fake-data'
import { mapMenusToMenus, mapMenusToRoutes } from '@/utils/system-permission'
import { apiLoginCode, apiLoginPassword, apiLoginRegister } from '@/apis/login'

import { localCache, sessionCache } from '@/utils/comm'
import {
  ACCESS_TOKEN,
  USER_ID,
  USER_SYSTEM_ROLE,
  PROJECT_CURT_BIG_TYPE,
  TEAM_CURT_INFO,
  PROJECT_CURT_SMALL_TYPE,
  PROJECT_OR_PLAN_PROCESS_MODULE_SEARCH
} from '@/global/constants.ts'

import ApiUser from '@/apis/user'
import type { IUserState } from './types.ts'
import type { IApiUser } from '@/apis/user/type.ts'

const useUserStore = defineStore('user', {
  state: (): IUserState => ({
    token: localCache.getCache(ACCESS_TOKEN) ?? '',
    userId: localCache.getCache(USER_ID) ?? '',
    userSysRole: sessionCache.getCache(USER_SYSTEM_ROLE) ?? '',
    userMenus: [],
    userInfo: {
      avatar: undefined, // 	用户头像
      nickname: undefined, // 	用户昵称
      mobile: undefined, // 用户手机号
      email: undefined, // 用户邮箱
      sex: undefined, // 性别
      createTime: undefined // 注册时间
    }
  }),

  actions: {
    // 注册 （账号 验证码 密码）
    async actionRegister(formData: any) {
      return await apiLoginRegister({
        mobile: formData.mobile,
        password: formData.password,
        code: formData.code
      })
    },

    // 登录（账号+密码 / 账号+验证码）
    async actionLogin(way: number, formData?: any) {
      let resToken: any

      try {
        // // 1.登录获取 token，并存
        switch (way) {
          case 1: // 密码登录
            resToken = await apiLoginPassword({ username: formData.mobile, password: formData.password })
            this.setUserCache({ token: resToken?.data?.accessToken, userId: resToken.data.userId })
            break
          case 2: // 验证码登录
            resToken = await apiLoginCode({ mobile: formData.mobile, code: formData.code })
            this.setUserCache({ token: resToken?.data?.accessToken, userId: resToken.data.userId })
            break
        }
        //
        if (resToken.code == 0) {
          // // 2.token 换 用户信息，并存
          await this.actionGetUserInfo()
        }
      } catch (e: any) {
        throw new Error(e)
      }
    },
    // 登录（选择 身份）
    async actionIdentity(userSysRole?: 'SERVICE' | 'BRAND') {
      sessionCache.removeCache(TEAM_CURT_INFO)
      sessionCache.removeCache(PROJECT_OR_PLAN_PROCESS_MODULE_SEARCH)
      this.setUserCache({ userSysRole })
    },

    // 登录（退出登录）
    actionLogout() {
      localCache.removeCache(ACCESS_TOKEN)
      localCache.removeCache(USER_ID)
      sessionCache.removeCache(USER_SYSTEM_ROLE)
      sessionCache.removeCache(TEAM_CURT_INFO)
      sessionCache.removeCache(PROJECT_CURT_BIG_TYPE)
      sessionCache.removeCache(PROJECT_CURT_SMALL_TYPE)
      sessionCache.removeCache(PROJECT_OR_PLAN_PROCESS_MODULE_SEARCH)
    },

    // 用户（获取用户信息）
    async actionGetUserInfo() {
      const resUser = await ApiUser.getUserInfo()
      this.setUserCache({ userInfo: resUser.data })
    },

    // 用户（修改用户信息）
    async actionEditUserInfo(params: IApiUser.TEditUserInfo) {
      await ApiUser.editUserInfo(params)
      await this.actionGetUserInfo()
    },

    // 用户（修改用户密码）
    async actionEditUserPassword(params: IApiUser.TEditUserPassword) {
      await ApiUser.editUserPassword(params)
    },

    // 用户（重置密码 - 忘记密码）
    async actionResetPassword(params: IApiUser.TEditUserPassword) {
      await ApiUser.forgetPassword(params)
    },

    async actionGetUserRouter(path?: string) {
      return new Promise(resolve => {
        const user_sys_role = sessionCache.getCache(USER_SYSTEM_ROLE)

        if (path && (path == '/' || path == '/index')) {
          sessionCache.setCache(USER_SYSTEM_ROLE, user_sys_role || 'SERVICE')
          this.userSysRole = user_sys_role || 'SERVICE'
        }

        if (path?.includes('services') || path?.includes('brands')) {
          const roleArr = path?.split?.('/')
          const p = roleArr?.[1]?.slice(0, -1)
          if (p == 'service' || p == 'brand') {
            sessionCache.setCache(USER_SYSTEM_ROLE, p.toUpperCase())
            this.userSysRole = p.toUpperCase() as 'SERVICE' | 'BRAND'
          }
        }

        // 3.1 重新映射左侧菜单
        const routerArr = this.userSysRole == 'SERVICE' ? [...fakeRouterServices, ...fakeRouterOthers] : [...fakeRouterBrands, ...fakeRouterOthers]

        const resMenus = mapMenusToMenus(routerArr)
        this.setUserCache({ userMenus: resMenus })

        // 3.2 获取当前用户所有 路由权限
        const routerList = mapMenusToRoutes(routerArr)
        resolve(routerList)
      })
    },

    // Set user`s catch
    setUserCache(params?: Partial<IUserState>) {
      if (params?.token) {
        localCache.setCache(ACCESS_TOKEN, params.token)
        this.token = params.token
      }

      if (params?.userId) {
        localCache.setCache(USER_ID, params.userId)
        this.userId = params.userId
      }

      if (params?.userSysRole) {
        sessionCache.setCache(USER_SYSTEM_ROLE, params.userSysRole)
        this.userSysRole = params.userSysRole
      }

      if (params?.userInfo) {
        Object.keys(this.userInfo).forEach((key: string) => {
          this.userInfo[key] = params.userInfo?.[key] ?? ''
        })
      }

      if (params?.userMenus) {
        this.userMenus = params.userMenus
      }
    }
  }
})

export default useUserStore
